import React, { useEffect, useRef, useState } from "react";
import { Box, InputBase } from "@mui/material";
import openEye from "../../images/eye-empty.svg";
import closeEye from "../../images/eye-password-hide.svg";
import InputWrapper, { inputButtonStyle } from "./inputWrapper";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { setFocusTo } from "../../Redux/Slices/focusOnValidation.slice";
import calendar from "../../images/calendar-icon.svg";
import { Colors } from "../../Constants/colors";
interface Props {
  value: string | number | null;
  setValue: (value: any) => unknown;
  label: string;
  placeHolder?: string;
  type?: string;
  isPassword?: boolean;
  isDateAndTime?: boolean;
  wrapperClassName?: string;
  focus?: string | null;
  name?: string;
  onMouseOut?: () => {};
  isRequiredFocusonValidation?: boolean;
  disabled?: boolean;
  onClick?: () => unknown;
  headerComponent?: React.ReactNode | null;
}

export enum InputType {
  Number = "number",
  Text = "text",
}

const InputField = ({
  value,
  setValue,
  label,
  placeHolder,
  type = InputType.Text,
  isPassword,
  isDateAndTime = false,
  wrapperClassName = "",
  focus = null,
  name = "",
  isRequiredFocusonValidation = false,
  disabled = false,
  onClick = () => {},
  headerComponent = null,
}: Props) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [inputType, setInputType] = useState<string>("text");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isPassword) return setInputType("text");

    if (isPassword && !showPassword) {
      setInputType("password");
    } else {
      setInputType("text");
    }
  }, [showPassword, isPassword]);

  const { focusTo } = useSelector((state: RootState) => ({
    focusTo: state.focusOnValidation.focusTo,
  }));

  const dispatch = useDispatch();

  useEffect(() => {
    if (isRequiredFocusonValidation) {
      if (inputRef && inputRef.current && inputRef.current.name === focusTo) {
        inputRef.current.focus();
      }
    }
    return () => {
      dispatch(setFocusTo(null));
    };
  }, [isRequiredFocusonValidation, focusTo, dispatch]);

  useEffect(() => {
    if (focus) {
      if (
        inputRef &&
        inputRef.current &&
        focus &&
        inputRef.current.name === focus
      ) {
        inputRef.current.focus();
      }
    }
  }, [focus]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (e && e.target) {
      setValue(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If type is "number", block alphabets

    if (type === InputType.Number) {
      if (e.metaKey || e.ctrlKey) {
        return;
      }

      if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
      }
    }
  };

  const getBorderColor = () => {
    if (disabled) return Colors.LightGrey;
    if (isFocused) return Colors.TangerineOrange;
    if (isHovered) return Colors.Grey;
    return Colors.LightGrey;
  };

  const getBackgroundColor = () => {
    if (disabled) return Colors.WhiteSmoke;
    if (isHovered) return Colors.VeryLightGrey;
    return Colors.White;
  };

  return (
    <InputWrapper
      label={label}
      wrapperStyle={{ width: "100%" }}
      wrapperClassName={wrapperClassName}
      headerComponent={headerComponent}
    >
      <InputBase
        onClick={onClick}
        placeholder={placeHolder}
        value={value}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
          handleKeyDown(e)
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange(e)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          ...inputButtonStyle,
          borderColor: getBorderColor(),
          backgroundColor: getBackgroundColor(),
          boxShadow: isFocused
            ? `0 0 0 3px ${Colors.LIGHT_TANGERINE_ORANGE}`
            : isHovered
              ? "0 2px 8px rgba(0, 0, 0, 0.08)"
              : "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        type={inputType}
        inputRef={inputRef}
        name={name}
        disabled={disabled}
      />

      {isPassword && (
        <Box right={10} bottom={12} position={"absolute"}>
          <img
            src={showPassword ? closeEye : openEye}
            alt="eye"
            style={propsButton}
            onClick={() => setShowPassword(!showPassword)}
          />
        </Box>
      )}
      {isDateAndTime && (
        <Box right={12} bottom={14} position={"absolute"}>
          <img src={calendar} alt="calendar" style={propsButton} />
        </Box>
      )}
    </InputWrapper>
  );
};

export const propsButton = {
  width: "16px",
  height: "16px",
  cursor: "pointer",
};

export default InputField;
